import type { DeviceApi } from '../api/device-api.js';
import type { TimeSource } from '../clock.js';
import type { Logger } from '../log.js';
import type { EventQueue } from './queue.js';
import type { EventBatchWire, GapMarkerWire, PlayEventWire } from './types.js';

/**
 * Uploads the queue in batches of `settings.event_batch_size` and deletes only on
 * server ack (technical-design §11.6). Timestamps are rendered here, from the raw
 * device clock plus the offset learned since the event was recorded.
 */

export interface EventUploader {
  flush(): Promise<{ readonly uploaded: number; readonly outcome: string }>;
}

export interface EventUploaderOptions {
  readonly queue: EventQueue;
  readonly api: DeviceApi;
  readonly time: TimeSource;
  readonly batchSize: () => number;
  readonly logger: Logger;
}

export function createEventUploader(options: EventUploaderOptions): EventUploader {
  let uploading = false;

  return {
    async flush() {
      if (uploading) return { uploaded: 0, outcome: 'already_uploading' };
      uploading = true;
      try {
        let uploaded = 0;
        for (;;) {
          const pending = await options.queue.peek(options.batchSize());
          if (pending.length === 0) return { uploaded, outcome: 'drained' };

          const events: PlayEventWire[] = [];
          const gaps: GapMarkerWire[] = [];
          for (const { record } of pending) {
            if (record.kind === 'play') {
              events.push({
                id: record.id,
                screen_id: record.screen_id,
                item_id: record.item_id,
                campaign_id: record.campaign_id,
                creative_id: record.creative_id,
                venue_id: record.venue_id,
                started_at: options.time.isoFromDeviceMs(record.started_at_device_ms),
                ended_at: options.time.isoFromDeviceMs(record.ended_at_device_ms),
                completed: record.completed,
                manifest_ver: record.manifest_ver,
                clock_unsynced: record.clock_unsynced,
              });
            } else {
              gaps.push({
                id: record.id,
                screen_id: record.screen_id,
                dropped_events: record.dropped_events,
                from: options.time.isoFromDeviceMs(record.from_device_ms),
                to: options.time.isoFromDeviceMs(record.to_device_ms),
                recorded_at: options.time.isoFromDeviceMs(record.recorded_at_device_ms),
                clock_unsynced: record.clock_unsynced,
              });
            }
          }

          const batch: EventBatchWire = { events, gaps };
          const result = await options.api.postEvents(batch);

          if (result.status === 'accepted') {
            await options.queue.ack(pending.map((entry) => entry.seq));
            uploaded += pending.length;
            continue;
          }

          if (result.status === 'unavailable') {
            options.logger.info('events.upload_deferred', {
              reason: result.reason,
              pending: pending.length,
            });
            return { uploaded, outcome: 'offline' };
          }

          if (result.status === 'revoked') {
            options.logger.error('events.upload_revoked', { pending: pending.length });
            return { uploaded, outcome: 'revoked' };
          }

          // A 4xx is the server's to fix. Nothing is acked and nothing is dropped:
          // the queue is the proof of play and losing it costs money.
          options.logger.error('events.upload_rejected', {
            http_status: result.http_status,
            pending: pending.length,
          });
          return { uploaded, outcome: `rejected_${String(result.http_status)}` };
        }
      } finally {
        uploading = false;
      }
    },
  };
}
